import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtTL } from "@/lib/api";
import BulkTahakkukDialog from "@/components/BulkTahakkukDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MessageCircle, Mail, Eye, Check, Undo2, Loader2, Upload } from "lucide-react";

const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const periodLabel = (p, tur) => { const [y, m] = (p || "").split("-"); if (!AYLAR[+m - 1]) return p || "-"; return tur === "YILLIK BEYANNAME" || tur === "KURUMLAR" ? `${y} yılı` : `${AYLAR[+m - 1]} ${y}`; };
const fmtDT = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};
const periodOptions = () => {
  const out = []; const d = new Date(); d.setDate(1);
  for (let i = 0; i < 14; i++) { out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); d.setMonth(d.getMonth() - 1); }
  return out;
};

export default function TahakkukGonderim() {
  const nav = useNavigate();
  const [show, setShow] = useState("unsent");
  const [period, setPeriod] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ rows: [], unsent_total: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // `${id}:${action}`
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/tahakkuk/queue", { params: { show, period: period || undefined } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Liste yüklenemedi"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [show, period]);

  const changed = () => { load(); window.dispatchEvent(new Event("tahakkuk:updated")); };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? data.rows.filter((r) => (r.unvan || "").toLowerCase().includes(q)) : data.rows;
  }, [data, search]);

  const view = async (r) => {
    const w = window.open("", "_blank");
    try {
      const res = await api.get(`/clients/${r.client_id}/documents/${r.id}/download`, { params: { inline: 1 }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type || "application/pdf" }));
      if (w) w.location.href = url; else window.open(url, "_blank");
    } catch { if (w) w.close(); toast.error("Belge açılamadı"); }
  };

  const whatsapp = async (r) => {
    // Pencere tıklama anında açılır (açılır pencere engelleyicisine takılmasın)
    const w = window.open("", "_blank");
    setBusy(`${r.id}:wa`);
    try {
      const { data: d } = await api.post(`/clients/${r.client_id}/documents/${r.id}/share`, {});
      const text = encodeURIComponent(d.message);
      const url = d.phone ? `https://wa.me/${d.phone}?text=${text}` : `https://wa.me/?text=${text}`;
      if (!d.phone) toast.info("Bu mükellefin geçerli telefonu yok. WhatsApp'ta kişiyi kendiniz seçin.");
      if (w) w.location.href = url; else window.open(url, "_blank");
      changed();
    } catch (e) {
      if (w) w.close();
      toast.error(e.response?.data?.detail || "WhatsApp bağlantısı oluşturulamadı");
    } finally { setBusy(null); }
  };

  const email = async (r) => {
    if (!r.email) { toast.error("Bu mükellefin kayıtlı e-posta adresi yok."); return; }
    if (!window.confirm(`${r.unvan} — ${r.tur} (${periodLabel(r.period, r.tur)}) tahakkuku ${r.email} adresine gönderilsin mi?`)) return;
    setBusy(`${r.id}:mail`);
    try {
      await api.post(`/clients/${r.client_id}/documents/${r.id}/email`, {});
      toast.success(`${r.email} adresine gönderildi`);
      changed();
    } catch (e) { toast.error(e.response?.data?.detail || "E-posta gönderilemedi"); }
    finally { setBusy(null); }
  };

  const setSent = async (r, sent) => {
    setBusy(`${r.id}:flag`);
    try { await api.post(`/tahakkuk/${r.id}/sent`, { sent }); changed(); }
    catch { toast.error("İşlem başarısız"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-5 fade-in">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-3xl font-semibold tracking-tight">Tahakkuk Gönderimi</h1>
          <p className="text-sm text-muted-foreground mt-1">Yüklenen tahakkukları mükelleflere WhatsApp veya e-posta ile iletin; iletilenler otomatik işaretlenir.</p>
        </div>
        <Button data-testid="bulk-upload-btn" onClick={() => setBulkOpen(true)}><Upload size={16} className="mr-1.5" /> Toplu PDF Yükle</Button>
        <Card className="px-5 py-3 text-right">
          <div className="text-xs uppercase text-muted-foreground tracking-wide">İletilmeyi bekleyen</div>
          <div className={`font-head text-2xl font-semibold ${data.unsent_total > 0 ? "text-amber-600" : "text-emerald-600"}`} data-testid="tah-unsent-total">{data.unsent_total}</div>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <select data-testid="tahq-show" value={show} onChange={(e) => setShow(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
          <option value="unsent">Sadece iletilmemişler</option>
          <option value="all">Tümü</option>
        </select>
        <select data-testid="tahq-period" value={period} onChange={(e) => setPeriod(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
          <option value="">Tüm dönemler</option>
          {periodOptions().map((p) => <option key={p} value={p}>{periodLabel(p)}</option>)}
        </select>
        <Input data-testid="tahq-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Mükellef ara..." className="w-56" />
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Mükellef</th>
                <th className="text-left px-3 py-3 font-medium">Tahakkuk</th>
                <th className="text-left px-3 py-3 font-medium">Dönem</th>
                <th className="text-right px-3 py-3 font-medium">Tutar</th>
                <th className="text-left px-3 py-3 font-medium">Durum</th>
                <th className="text-right px-4 py-3 font-medium">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">Yükleniyor...</td></tr>}
              {!loading && rows.map((r) => (
                <tr key={r.id} data-testid={`tahq-row-${r.id}`} className="border-t border-border hover:bg-accent/40">
                  <td className="px-4 py-2.5">
                    <button className="font-medium hover:underline text-left" onClick={() => nav(`/mukellefler/${r.client_id}`)}>{r.unvan}</button>
                    {!r.has_phone && <div className="text-[11px] text-amber-600">Geçerli telefon yok</div>}
                  </td>
                  <td className="px-3 py-2.5"><span className="text-xs px-2 py-0.5 rounded bg-secondary">{r.tur || "-"}</span></td>
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{periodLabel(r.period, r.tur)}</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap text-muted-foreground">{r.tutar != null ? fmtTL(r.tutar) : "-"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {r.sent_at
                      ? <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">İletildi · {r.sent_via} · {fmtDT(r.sent_at)}</span>
                      : <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">Bekliyor</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button title="Belgeyi görüntüle" onClick={() => view(r)} className="text-muted-foreground hover:text-blue-500"><Eye size={16} /></button>
                      <Button size="sm" variant="outline" data-testid={`tahq-wa-${r.id}`} disabled={busy === `${r.id}:wa`} onClick={() => whatsapp(r)}
                        title={r.telefon ? `${r.telefon} numarasına WhatsApp ile gönder` : "Telefon kayıtlı değil"} className="text-green-700 border-green-300 hover:bg-green-50">
                        {busy === `${r.id}:wa` ? <Loader2 size={14} className="mr-1 animate-spin" /> : <MessageCircle size={14} className="mr-1" />} WhatsApp
                      </Button>
                      <button title={r.email ? `${r.email} adresine e-posta ile gönder` : "E-posta adresi yok"} disabled={busy === `${r.id}:mail`} onClick={() => email(r)} className="text-muted-foreground hover:text-blue-600 disabled:opacity-50">
                        {busy === `${r.id}:mail` ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                      </button>
                      {r.sent_at
                        ? <button title="İletildi işaretini geri al" disabled={busy === `${r.id}:flag`} onClick={() => setSent(r, false)} className="text-muted-foreground hover:text-amber-600"><Undo2 size={16} /></button>
                        : <button title="Başka yoldan ilettim, iletildi olarak işaretle" disabled={busy === `${r.id}:flag`} onClick={() => setSent(r, true)} className="text-muted-foreground hover:text-emerald-600"><Check size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">
                  {show === "unsent" ? "İletilmeyi bekleyen tahakkuk yok. 🎉" : "Kayıtlı tahakkuk yok. Mükellef sayfasındaki Tahakkuklar sekmesinden yükleyin."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      <BulkTahakkukDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={load} />
      <p className="text-xs text-muted-foreground">WhatsApp'a tıklayınca mesaj hazırlanır ve tahakkuk "iletildi" işaretlenir. Mesajı gerçekten göndermediyseniz sağdaki geri al simgesini kullanın.</p>
    </div>
  );
}
